import type { PlanChangePreview } from '../../services/api';
import type { Translate } from './profileTypes';

/**
 * Confirm-dialog copy for a plan change, from GET /api/billing/change-plan/preview
 * (BILLING_LIFECYCLE_SPEC.md BIL-L05):
 * - upgrades: the amount Stripe charges today, the renewal date and price;
 * - downgrades: "No charge today. Your plan changes to X on <date>; you keep Y until then.";
 * - a scheduled pause is canceled by any plan change, and the dialog says so.
 */
export interface PlanChangeCopy {
  /** `same_plan` is a no-op (not confirmable); `keep_plan` undoes a pending change or cancel. */
  kind: 'upgrade' | 'downgrade' | 'trial' | 'keep_plan' | 'same_plan' | 'fallback';
  title: string;
  message: string;
  dueToday: string;
  renewal: string | null;
  acknowledgement: string;
  pauseNotice: string | null;
}

export interface PlanChangeCopyOptions {
  t: Translate;
  /** Formats an amount in the smallest currency unit. */
  formatMoney: (cents: number | null | undefined, currency?: string | null) => string;
  /** Formats an ISO date for display. */
  formatDate: (iso?: string | null) => string;
  currentName: string;
  targetName: string;
  /** Display name of a pending downgrade's plan (same-plan previews only). */
  pendingName?: string;
}

export function describePlanChange(preview: PlanChangePreview, options: PlanChangeCopyOptions): PlanChangeCopy {
  const { t, formatMoney, formatDate, currentName, targetName, pendingName } = options;
  const dueToday = formatMoney(preview.amount_due_now, preview.currency);
  const renewalDate = formatDate(preview.renewal_date);
  const renewalPrice = formatMoney(preview.renewal_amount, preview.currency);
  const renewal = preview.renewal_date && typeof preview.renewal_amount === 'number'
    ? t('profile.planChangeRenewalNote', { date: renewalDate, price: renewalPrice })
    : null;
  const pauseNotice = preview.cancels_scheduled_pause ? t('profile.planChangePauseNotice') : null;

  if (preview.change_type === 'same_plan' && (preview.releases_pending_change || preview.clears_scheduled_cancel)) {
    // Re-selecting the current plan undoes a pending downgrade (BIL-L07) or a scheduled cancel (BIL-L09).
    return {
      kind: 'keep_plan',
      title: t('profile.planChangeNoChargeTitle'),
      message: preview.releases_pending_change
        ? t('profile.planChangeKeepPlanMessage', { current: currentName, target: pendingName || targetName })
        : t('profile.planChangeKeepRenewingMessage', { current: currentName, date: renewalDate }),
      dueToday,
      renewal,
      acknowledgement: t('profile.planAcknowledgeKeepPlanLabel'),
      pauseNotice,
    };
  }

  if (preview.change_type === 'same_plan') {
    return {
      kind: 'same_plan',
      title: t('profile.planChangeSamePlanTitle'),
      message: t('profile.planChangeSamePlanMessage', { current: currentName }),
      dueToday,
      renewal,
      acknowledgement: t('profile.planAcknowledgeDowngradeLabel'),
      pauseNotice: null,
    };
  }

  if (preview.change_type === 'downgrade') {
    return {
      kind: 'downgrade',
      title: t('profile.planChangeNoChargeTitle'),
      message: t('profile.planChangeDowngradeMessage', {
        current: currentName,
        target: targetName,
        date: formatDate(preview.effective_at || preview.renewal_date),
      }),
      dueToday,
      renewal,
      acknowledgement: t('profile.planAcknowledgeDowngradeLabel'),
      pauseNotice,
    };
  }

  if (preview.trialing) {
    return {
      kind: 'trial',
      title: t('profile.planChangeNoChargeTitle'),
      message: t('profile.planChangeTrialMessage', { target: targetName, date: renewalDate, price: renewalPrice }),
      dueToday,
      renewal,
      acknowledgement: t('profile.planAcknowledgeTrialLabel'),
      pauseNotice,
    };
  }

  return {
    kind: 'upgrade',
    title: t('profile.planChangeUpgradeTitle'),
    message: t(
      preview.new_period_starts_now ? 'profile.planChangeUpgradeNewPeriodMessage' : 'profile.planChangeUpgradeMessage',
      { current: currentName, target: targetName, amount: dueToday },
    ),
    dueToday,
    renewal,
    acknowledgement: t('profile.planAcknowledgeUpgradeLabel', { amount: dueToday }),
    pauseNotice,
  };
}

/**
 * Copy when the preview failed or timed out: the change can still be confirmed (without a
 * proration date) and the exact amount appears on the invoice.
 */
export function fallbackPlanChangeCopy(t: Translate): PlanChangeCopy {
  return {
    kind: 'fallback',
    title: t('profile.planChangeFallbackTitle'),
    message: t('profile.planChangeFallbackMessage'),
    dueToday: t('profile.planChangeFallbackDueToday'),
    renewal: null,
    acknowledgement: t('profile.planAcknowledgeFallbackLabel'),
    pauseNotice: t('profile.planChangeFallbackPauseNotice'),
  };
}

/** One paragraph for a plain confirm dialog (plan selection's "change plan" path). */
export function planChangeSummary(copy: PlanChangeCopy): string {
  return [copy.message, copy.renewal, copy.pauseNotice].filter(Boolean).join(' ');
}
